import { Suspense } from 'react';
import { QueryProvider } from './QueryProvider';
import { AuthProvider } from './AuthProvider';
import { ThemeProvider } from './ThemeProvider';
import { TooltipProvider } from '@/components/Tooltip';
import { NotificationProvider } from './NotificationProvider';
import { GlobalWebSocketProvider } from '@/features/live-chat/providers/GlobalWebSocketProvider';
import { SubscriptionModalProvider } from './SubscriptionModalProvider';
import { createBrowserRouter, Outlet, RouteObject, RouterProvider } from 'react-router-dom';
import MinimalLayout from '@/components/layout/MinimalLayout';
import NotFound from '@/features/error/pages/NotFound';
import { Loader } from 'lucide-react';
import { routes } from '../router/routes';
import Layout from '@/components/layout/Layout';
import { useAppUpdate } from '@/hooks/useAppUpdate';
import { AppUpdatePopup } from '@/components/AppUpdatePopup';
import ChatbotDedicatedPage from '@/features/chatbots/pages/ChatbotDedicatedPage';

const AppUpdateNotifier = () => {
  const { isUpdateAvailable, triggerUpdate, currentVersion } = useAppUpdate();

  return (
    <AppUpdatePopup
      isOpen={isUpdateAvailable}
      onUpdate={triggerUpdate}
      version={currentVersion!}
    />
  );
};

import { GoogleAnalytics } from '@/components/GoogleAnalytics';

// ✅ Lives inside the router tree — useNavigate works here
const RouterInnerProviders = () => (
  <SubscriptionModalProvider>
    <GlobalWebSocketProvider>
      <NotificationProvider>
        <GoogleAnalytics />
        <Outlet />
      </NotificationProvider>
    </GlobalWebSocketProvider>
  </SubscriptionModalProvider>
);

const SuspenseWrapper = () => (
  <Suspense
    fallback={
      <div className="flex items-center justify-center min-h-screen">
        <Loader size={48} />
      </div>
    }
  >
    <Outlet />
  </Suspense>
);

const router = createBrowserRouter([
  {
    element: <SuspenseWrapper />,
    children: [
      {
        // ✅ RouterInnerProviders wraps all routes — gives them router context
        element: <RouterInnerProviders />,
        children: [
          {
            element: <MinimalLayout />,
            children: [
              { path: '/chatbot/:id/view', element: <ChatbotDedicatedPage /> },
              { path: '*', element: <NotFound /> },
            ],
          },
          {
            element: <Layout />,
            children: routes.filter(
              (r) => r.path !== '/chatbot/:id/view' && r.path !== '*'
            ) as RouteObject[],
          },
        ],
      },
    ],
  },
]);

export default function AppProviders() {
  return (
    <QueryProvider>
      <ThemeProvider>
        <TooltipProvider>
          <AuthProvider>
            <RouterProvider router={router} />
            <AppUpdateNotifier />
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryProvider>
  );
}